import { create } from 'zustand'

import { useSessionStore } from '@/lib/auth/session-store'

interface ChatUiState {
  /** Preserved chat-list search term across tab switches (D-043). */
  search: string
  /** Preserved "past activities" expansion state. */
  archivedOpen: boolean
  /** Per-chat composer drafts, kept in memory only. */
  drafts: Record<string, string>
  /** Chat-list window scroll offset across tab switches. */
  listScrollY: number
  setSearch: (search: string) => void
  setArchivedOpen: (open: boolean) => void
  setListScrollY: (offset: number) => void
  setDraft: (key: string, value: string) => void
  clearDraft: (key: string) => void
  reset: () => void
}

/**
 * Local chat UI state only (D-042): search, expansion and in-memory drafts.
 * Never store API responses, message lists or unread counts here.
 */
export const useChatUiStore = create<ChatUiState>()((set) => ({
  search: '',
  archivedOpen: false,
  drafts: {},
  listScrollY: 0,
  setSearch: (search) => set({ search }),
  setArchivedOpen: (archivedOpen) => set({ archivedOpen }),
  setListScrollY: (listScrollY) => set({ listScrollY }),
  setDraft: (key, value) =>
    set((state) => ({ drafts: { ...state.drafts, [key]: value } })),
  clearDraft: (key) =>
    set((state) => {
      if (!(key in state.drafts)) return state
      const drafts = { ...state.drafts }
      delete drafts[key]
      return { drafts }
    }),
  reset: () =>
    set({ search: '', archivedOpen: false, drafts: {}, listScrollY: 0 }),
}))

export function activityDraftKey(activityId: string): string {
  return `activity:${activityId}`
}

export function conversationDraftKey(conversationId: string): string {
  return `conversation:${conversationId}`
}

// Drafts are sensitive in-memory user content: never let them outlive the
// signed-in identity that typed them.
let lastUserId = useSessionStore.getState().user?.id ?? null
useSessionStore.subscribe((state) => {
  const userId = state.user?.id ?? null
  if (userId === lastUserId) return
  lastUserId = userId
  useChatUiStore.getState().reset()
})
